import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router";
import { createClassForHSForm } from "../../utils/requests/form";
import Form from "../../utils/forms/handleForm";
import { postCompletion } from "../../utils/requests/completions";
import { CompletionsContext, ThemeContext, ContactIdContext, LoadingContext } from "../../App"
import { FormTitleContext } from "../../layout/Vertical/Global";
import { MsalInstanceContext, VariablesContext } from "../../auth/AppProvider";
import { SetTrakingHubspot } from "../../utils/functions/trackingHubspot";
import HandleErrorsMessage from "./HandleErrorsMessage";
import { withTranslation } from "react-i18next";
import { getStripeStatus } from "../../utils/requests/stripe";
import { CheckIcon, BellAlertIcon } from "@heroicons/react/24/outline";
import Alert from "../../components/Inputs/alert/Alert.js";

export const FormLoadingContext = React.createContext();

function Completion({ t }) {
    const [countdown, setCountdown] = useState({
        days: '00',
        hours: '00',
        minutes: '00',
        seconds: '00',
    });
    const scrollToTop = useRef()
    const stepperRefs = useRef([]);
    const stepperContainerRef = useRef();
    const [completion, setCompletion] = useState({});
    const [, setFormTitle] = useContext(FormTitleContext);
    const [step, setStep] = useState(0);
    const [form, setForm] = useState({});
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState(null);
    const [pageLoading, setPageLoading] = useContext(LoadingContext);
    const [formLoading, setFormLoading] = useState(false);
    const [infoSaved, setInfoSaved] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [stepWorkingOn, setStepWorkingOn] = useState(-1);

    let navigate = useNavigate();
    let { id, checkoutid } = useParams();
    const location = useLocation();

    const [completions, setCompletions] = useContext(CompletionsContext);
    const contactId = useContext(ContactIdContext);
    const theme = useContext(ThemeContext);
    const msalInstance = useContext(MsalInstanceContext);
    const variables = useContext(VariablesContext);

    useEffect(() => {
        setIsVisible(false);
        const timer = setTimeout(() => {
            setIsVisible(true);
        }, 250);

        return () => clearTimeout(timer);

    }, []);

    const stepRef = useRef(0);
    stepRef.current = step;

    function centerActiveStep() {
        var currentStep = stepRef.current;
        var btn = stepperRefs.current[currentStep];
        var container = stepperContainerRef.current;
        if (btn && container && container.scrollWidth > container.clientWidth) {
            var btnRect = btn.getBoundingClientRect();
            var containerRect = container.getBoundingClientRect();
            var btnCenterInContent = container.scrollLeft + (btnRect.left - containerRect.left) + btnRect.width / 2;
            var targetScroll = btnCenterInContent - container.clientWidth / 2;
            container.scrollTo({ left: Math.max(0, targetScroll), behavior: 'smooth' });
        }
    }

    useEffect(() => {
        setTimeout(centerActiveStep, 100);
    }, [step]);

    useEffect(() => {
        if (completion?.steps?.length) {
            // Multiple attempts to handle layout/animation timing
            setTimeout(centerActiveStep, 300);
            setTimeout(centerActiveStep, 800);
        }
    }, [completion]);

    useEffect(() => {
        if (isVisible && completion?.steps?.length) {
            setTimeout(centerActiveStep, 100);
        }
    }, [isVisible]);

    useEffect(() => {
        if (isVisible && completion?.finishedInfo?.finished && !completion?.finishedInfo?.canStillEdit) {
            navigate("/" + id);
            return;
        }
        if (isVisible && !pageLoading && !loading && (!completion?.steps?.length || !form?.questions)) {
            navigate("/" + id);
        }
    }, [isVisible, pageLoading, loading, completion, form, navigate, id]);

    // Mouse drag to scroll the stepper horizontally on desktop
    useEffect(() => {
        var container = stepperContainerRef.current;
        if (!container) return;
        var isDown = false;
        var startX = 0;
        var scrollLeftStart = 0;

        function onMouseDown(e) {
            isDown = true;
            startX = e.pageX - container.offsetLeft;
            scrollLeftStart = container.scrollLeft;
            container.style.cursor = 'grabbing';
            container.style.userSelect = 'none';
        }
        function onMouseLeave() {
            isDown = false;
            container.style.cursor = 'grab';
            container.style.userSelect = '';
        }
        function onMouseUp() {
            isDown = false;
            container.style.cursor = 'grab';
            container.style.userSelect = '';
        }
        function onMouseMove(e) {
            if (!isDown) return;
            e.preventDefault();
            var x = e.pageX - container.offsetLeft;
            container.scrollLeft = scrollLeftStart - (x - startX);
        }

        container.style.cursor = 'grab';
        container.addEventListener('mousedown', onMouseDown);
        container.addEventListener('mouseleave', onMouseLeave);
        container.addEventListener('mouseup', onMouseUp);
        container.addEventListener('mousemove', onMouseMove);
        return () => {
            container.removeEventListener('mousedown', onMouseDown);
            container.removeEventListener('mouseleave', onMouseLeave);
            container.removeEventListener('mouseup', onMouseUp);
            container.removeEventListener('mousemove', onMouseMove);
        };
    }, []);

    function toTheTop() {
        if (scrollToTop.current) {
            scrollToTop.current.scrollIntoView({ behavior: 'smooth' });
        }
    }

    function getTimeRemaining(date) {
        const target = new Date(date + "T23:59:59");
        const time = target.getTime() - new Date().getTime(),
            days = Math.floor(time / 1000 / 60 / 60 / 24),
            hours = Math.floor((time / 1000 / 60 / 60) % 24),
            minutes = Math.floor((time / 1000 / 60) % 60),
            seconds = Math.floor((time / 1000) % 60);
        return {
            time,
            days,
            hours,
            minutes,
            seconds,
        };
    }

    useEffect(() => {
        createClassForHSForm(theme);
        var completionCurr = completions && completions.find(c => c.id === id);
        if (completionCurr?.finishedInfo?.finished && !completionCurr?.finishedInfo?.canStillEdit) return;
        var formCurr = {};
        if (completionCurr && completionCurr.steps && completionCurr.steps[0] && completionCurr.objectAssociated != null) {
            if (checkoutid && checkoutid !== "cancel") {
                setPageLoading(true);
                if (completionCurr.steps.find(st => st.questions.find(q => q.label === "Paybox"))) {
                    var payboxQuestionId = location.state?.payboxQuestionId;
                    var index = payboxQuestionId
                        ? completionCurr.steps.findIndex(st => st.questions.find(q => q.id === payboxQuestionId && q.label === "Paybox" && q.fieldType === "custom input"))
                        : -1;
                    if (index < 0) {
                        index = completionCurr.steps.findIndex(st => st.questions.find(q => q.label === "Paybox" && q.fieldType === "custom input"));
                    }
                    var apiStep = completionCurr.step || 0;
                    if (index > apiStep) {
                        index = completionCurr.steps.findIndex(st => st.questions.find(q => q.label === "Paybox" && q.fieldType === "custom input"));
                    }
                    if (index >= 0 && completionCurr.steps[index]) {
                        setStep(index);
                        setStepWorkingOn(index);
                        formCurr = completionCurr.steps[index];
                    } else {
                        formCurr = completionCurr.steps[0];
                    }
                    setCompletion(completionCurr);
                    setForm(formCurr);
                    setPageLoading(false);
                } else if (completionCurr.steps.find(st => st.questions.find(q => q.label === "Stripe"))) {
                    getStripeStatus(msalInstance, variables, completionCurr.objectAssociated.objectId, checkoutid).then(res => {
                        if (res) {
                            var index = completionCurr.steps.findIndex(st => st.questions.find(q => q.label === "Stripe" && q.fieldType === "custom input"))
                            if (index >= 0 && completionCurr.steps[index]) {
                                setStep(index);
                                setStepWorkingOn(index);
                                formCurr = completionCurr.steps[index];
                            } else {
                                formCurr = completionCurr.steps[0];
                            }
                            setCompletion(completionCurr);
                            setForm(formCurr);
                            setPageLoading(false);
                        } else {
                            navigate("/");
                        }
                    })
                }

            }
            else {
                var apiStep = completionCurr.step || 0;
                // Find highest accessible step at or below apiStep
                var initialStep = apiStep;
                for (let i = apiStep; i >= 0; i--) {
                    if (!completionCurr.steps[i]?.pipelinePrerequisite || completionCurr.steps[i].pipelinePrerequisite === completionCurr.hs_pipeline_stage) {
                        initialStep = i;
                        break;
                    }
                    if (i === 0) initialStep = 0;
                }
                if (location.state?.targetStep != null) {
                    var target = location.state.targetStep;
                    var targetStep = completionCurr.steps[target];
                    var targetLocked = targetStep?.pipelinePrerequisite && targetStep.pipelinePrerequisite !== completionCurr.hs_pipeline_stage;
                    if (!targetLocked && target >= 0 && target <= apiStep) {
                        var canGoToTarget = true;
                        for (let i = target + 1; i <= apiStep; i++) {
                            if (!completionCurr.steps[i]?.canGoBack) {
                                canGoToTarget = false;
                                break;
                            }
                        }
                        if (canGoToTarget) initialStep = target;
                    }
                }
                setStep(initialStep);
                setStepWorkingOn(initialStep);
                formCurr = completionCurr.steps[initialStep];
                setCompletion(completionCurr);
                setForm(formCurr);
            }
            var endDate = formCurr?.endDate;
            if (endDate) {
                const time = getTimeRemaining(completionCurr.variables?.find(v => v.id === endDate)?.value);
                setCountdown({
                    days: time.days,
                    hours: time.hours,
                    minutes: time.minutes,
                    seconds: time.seconds,
                });
            }
        } else {
            navigate("/")
        }
    }, [id, completions, theme, navigate, t, checkoutid, msalInstance, variables]);

    useEffect(() => {
        if (completion && completion.parameters) {
            setFormTitle(completion.label)
            SetTrakingHubspot(msalInstance, variables.HSTenantID, variables.ClientName, completion.label + " - Etape : " + form?.name);
        }
    }, [variables, completion, msalInstance, step, form])

    function handleChangeStep(locstep) {
        setInfoSaved(false);
        if (locstep > stepWorkingOn) {
            setStepWorkingOn(locstep);
        }
        setLoading(true);
        if (locstep < 0) {
            locstep = 0
        }
        setStep(() => {
            if (completion.steps && completion.steps.length >= locstep) {
                setForm(completion.steps[locstep])
                setErrors(null);
                setLoading(false);
                toTheTop()
                return locstep;
            } else {
                navigate("/");
            }
        });
        if (completion.steps[locstep]?.endDate) {
            const time = getTimeRemaining(completion.variables?.find(v => v.id === completion.steps[locstep].endDate)?.value);
            setCountdown({
                days: time.days,
                hours: time.hours,
                minutes: time.minutes,
                seconds: time.seconds,
            });
        }
    }

    function canNavigateBackTo(targetStep) {
        if (!completion?.steps || targetStep < 0 || targetStep >= step) return false;
        for (let i = targetStep + 1; i <= step; i++) {
            if (!completion.steps[i]?.canGoBack) {
                return false;
            }
        }
        return true;
    }

    function handleSt(res, newStep, cl) {
        var stepCL = Object.assign({}, res.steps.find(s => s?.questions?.find(sq => sq.id === cl.block)));
        var questionCL = Object.assign({}, stepCL?.questions?.find(sq => sq?.id === cl?.block));
        if (questionCL && Object.keys(questionCL).length > 0) {
            if (!newStep?.questions.find(q => q?.id === questionCL?.id)) {
                questionCL.hidden = true;
                questionCL.readonly = true;
                questionCL.required = false;
                questionCL.conditional = true;
                questionCL.conditionalList = [cl];
                newStep?.questions.push(questionCL)

            } else {
                var questionNIndex = newStep?.questions?.findIndex(q => q.id === questionCL.id);
                var questionN = newStep?.questions[questionNIndex]
                if (questionNIndex > -1) {
                    newStep?.questions?.splice(questionNIndex, 1);
                }
                questionN?.conditionalList?.push(cl);
                newStep.questions.splice(questionNIndex, 0, questionN);
            }
            if (cl.and) {
                newStep = handleSt(res, newStep, cl.and)
            }
        }
        return newStep
    }


    function handleSubmit(formData) {
        const readResponseBody = async (response) => {
            const raw = await response.text();
            if (!raw) {
                return null;
            }

            try {
                return JSON.parse(raw);
            } catch {
                return raw;
            }
        };

        setInfoSaved(false);
        setErrors(false);
        setLoading(true);
        var data = {};
        data.step = step;
        data.contactId = contactId;
        var cand = completion;
        cand.steps.forEach(step => {
            step.questions.forEach(question => {
                if (formData[question.name] !== undefined)
                    question.value = formData[question.name]
            })
        })
        data.candidature = cand;
        data.properties = cand.properties;
        return postCompletion(msalInstance, variables, data).then(async res => {
            if (res.ok) {
                const body = await readResponseBody(res);
                if (body && typeof body === "object") {
                    return body;
                }
                throw new Error(`Backend call failure (${res.status}): invalid JSON response`);
            } else {
                if (res.status === 401) {
                    const baseUrl = window.location.origin + '/';
                    window.location.replace(baseUrl);
                } else {
                    const err = await readResponseBody(res);
                    let errorMessage = `Backend call failure (${res.status})`;

                    if (err && typeof err === "object") {
                        if (err.noteError) {
                            completion.noteError = err.noteError;
                            setCompletion(completion);
                        }
                        if (typeof err.message === "string" && err.message.trim().length > 0) {
                            errorMessage = err.message;
                        }
                    } else if (typeof err === "string" && err.trim().length > 0) {
                        errorMessage = err;
                    }

                    setLoading(false);
                    throw new Error(errorMessage);
                }
            }
        }).then(res => {
            if (res) {
                try {
                    var index = completions.findIndex(c => c.id === res.id);
                    var completionsLocal = completions;
                    completionsLocal[index] = res;
                    setCompletions(completionsLocal)
                    setCompletion(res);
                    var resApiStep = res.step || 0;
                    var nextStepAccessible = (step + 1) <= resApiStep && res.steps[step + 1];
                    if ((resApiStep + 1 >= res.steps.length) || (res.steps && res.steps[step] && res.steps[step].canGoNext) || nextStepAccessible) {
                        setStep(s => {
                            if (res?.finishedInfo?.finished ) {
                                if (res.steps && res.steps.length === 1) {
                                    var newStep = res.steps[0]
                                    setForm(newStep);
                                    setLoading(false);
                                    setStepWorkingOn(0);
                                    return 0;
                                } else {
                                    navigate("/");
                                }
                                if (res?.finishedInfo?.canStillEdit)
                                    navigate("/" + res.id);
                            } else {
                                var stepToGo = resApiStep < s + 1 ? resApiStep : s + 1;
                                if (stepToGo >= res.steps.length) {
                                    navigate("/" + res.id);
                                } else {
                                    var newStep = res?.steps[stepToGo]
                                    if (newStep?.questions?.find(q => q?.fieldType === "custom input" && q?.object === "Logic")) {
                                        newStep?.questions.filter(q => q?.fieldType === "custom input" && q?.object === "Logic").forEach(q => {
                                            q?.conditionalList?.forEach(cl => {
                                                newStep = handleSt(res, newStep, cl)
                                            })
                                        })
                                    }
                                    var questions = [];
                                    newStep?.questions.forEach(q => {
                                        if (q.fieldType !== "custom input" && q.fieldType !== "custom variable" && q.optionsCustomList?.length !== 1 && res?.steps?.find(s => s?.questions?.find(qq => qq.name === q.name && qq.id !== q.id))) {
                                            q.value = res?.steps?.find(s => s?.questions?.find(qq => qq.name === q.name && qq.id !== q.id))?.questions?.find(qq => qq.name === q.name && qq.id !== q.id)?.value;
                                        }
                                        questions.push(q)
                                    })
                                    newStep.questions = questions
                                    setForm(newStep)
                                    setLoading(false);
                                    setStepWorkingOn(stepToGo);
                                    handleChangeStep(stepToGo);
                                    return stepToGo;
                                }
                            }
                        });
                    } else {
                        const currentStepRes = res.steps[step];
                        const nextStepRes = res.steps[step + 1];
                        const hasPipelineGap =
                            currentStepRes?.pipelineEnd &&
                            nextStepRes?.pipelinePrerequisite &&
                            currentStepRes.pipelineEnd !== nextStepRes.pipelinePrerequisite;

                        if (hasPipelineGap) {
                            navigate("/");
                        } else {
                            setLoading(false);
                            setInfoSaved(true);
                        }
                    }
                    return true
                }
                catch (e) {
                    navigate("/");
                }
            } else {
                throw false;
            }
        }).catch(err => {
            if (err != null && typeof err === "string")
                setErrors([err]);
            else if (err && err.message)
                setErrors([err.message]);
            else
                setErrors([t("Please refresh the page and try again")]);
            setLoading(false);
            throw err;
        })
    }

    return pageLoading || (completion && completion.steps && step >= completion.steps.length) ? null : <div className={isVisible ? "my-forms-visible layout" : "hidden"}>
        <div className="max-w-3xl mx-auto pt-10 pb-20" ref={scrollToTop}>
            {/* Header */}
            <div className="mb-5">
                <button
                    onClick={() => navigate("/" + completion.id)}
                    className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-gray-600 transition-colors mb-4"
                >
                    ← {t("Quitter")}
                </button>
                <p className="text-[13px] text-gray-400 mb-1">{t("Candidature pour")}</p>
                <h1 className="text-[clamp(1.75rem,3.5vw,2.5rem)] font-bold leading-[1.1] text-gray-900" lang="fr" translate="yes">{completion.label}</h1>
            </div>

            {/* Profile banner — shown when the step has contact fields */}
            {/* {form?.questions?.some(q => q.objectId === "0-1") && (
                <div
                    className="flex items-center gap-3 rounded-xl px-4 py-3 mb-6"
                    style={{ backgroundColor: "color-mix(in srgb, var(--primary-color) 8%, white)", border: "1px solid color-mix(in srgb, var(--primary-color) 20%, white)" }}
                >
                    <span
                        className="flex items-center justify-center w-7 h-7 rounded-lg flex-shrink-0 text-white text-sm"
                        style={{ backgroundColor: "var(--primary-color)" }}
                    >
                        ✦
                    </span>
                    <p className="text-sm text-gray-500 flex-1">
                        {(() => {
                            const filledLabels = form.questions
                                .filter(q => q.objectId === "0-1" && q.value && q.label)
                                .slice(0, 4)
                                .map(q => q.label);
                            return filledLabels.length > 0
                                ? `${filledLabels.join(", ")}... ${t("pré-remplis depuis votre profil. Vérifiez et complétez si besoin.")}`
                                : t("Vos informations sont pré-remplies depuis votre profil. Vérifiez et complétez si besoin.");
                        })()}
                    </p>
                    <button
                        // Ouvre la page profil pour corriger les données sources du pré-remplissage.
                        onClick={() => navigate("/profile")}
                        className="text-sm font-semibold whitespace-nowrap flex-shrink-0 hover:opacity-75 transition-opacity"
                        style={{ color: "var(--primary-color)" }}
                    >
                        {t("Modifier mon profil")} →
                    </button>
                </div>
            )} */}

            {/* Stepper */}
            <div className="mb-8 -mx-5 px-5">
                <div ref={stepperContainerRef} className="flex items-start gap-1 overflow-x-auto pb-1" style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}>
                    {completion?.steps?.map((stepL, stepIdx) => (
                        <button
                            key={stepIdx}
                            ref={el => stepperRefs.current[stepIdx] = el}
                            className="flex items-center gap-2 px-3 py-2 rounded-xl text-[13px] font-medium transition-all whitespace-nowrap flex-shrink-0"
                            style={stepIdx === step ? {
                                backgroundColor: 'color-mix(in srgb, var(--primary-color) 10%, white)',
                                color: 'var(--primary-color)',
                            } : stepIdx < stepWorkingOn ? {
                                color: '#626b7a',
                            } : {
                                color: '#8a93a2',
                            }}
                            onClick={() => {
                                if (stepIdx < stepWorkingOn && (stepIdx >= step || canNavigateBackTo(stepIdx)) && !loading) {
                                    handleChangeStep(stepIdx);
                                } else if (stepIdx === stepWorkingOn && stepWorkingOn !== step && !loading) {
                                    handleChangeStep(stepIdx);
                                }
                            }}
                        >
                            <span
                                className="flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-semibold flex-shrink-0"
                                style={stepIdx === step ? {
                                    backgroundColor: 'var(--primary-color)',
                                    color: '#fff',
                                } : stepIdx < stepWorkingOn ? {
                                    backgroundColor: 'color-mix(in srgb, var(--primary-color) 15%, white)',
                                    color: 'var(--primary-color)',
                                } : {
                                    backgroundColor: '#eaecf0',
                                    color: '#8a93a2',
                                }}
                            >
                                {stepIdx < stepWorkingOn ? (
                                    <CheckIcon className="h-3 w-3" />
                                ) : (
                                    <span>{stepIdx + 1}</span>
                                )}
                            </span>
                            <span lang="fr" translate="yes">{stepL.name}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Form card */}
            <div className="bg-white border border-gray-100 p-7 md:p-8 relative overflow-hidden" style={{ borderRadius: '20px', borderColor: '#eceef2', boxShadow: 'rgba(16, 20, 34, 0.3) 0px 22px 50px -40px' }}>
                {completion.steps?.[step]?.endDate && countdown.days < 7 ? <div className='text-xs text-bold text-red-500 flex items-center mb-4'>
                    <BellAlertIcon className="h-5 w-5 inline-block mr-1 tilt-shaking" />
                    {countdown.days > 0 ?
                        <span className='font-bold'>{countdown.days > 1 ? t("Only {{count}} days left to complete this step", { count: countdown.days }) : t("Only {{count}} day left to complete this step", { count: countdown.days })}</span> :
                        countdown.hours > 0 ?
                            <span className='font-bold'>{t("Only {{count}} hour left to complete this step", { count: countdown.hours })}</span> :
                            <span className='font-bold'>{t("Only {{count}} minute left to complete this step", { count: countdown.minutes })}</span>
                    }
                </div> : null}
                {errors ? <HandleErrorsMessage errors={errors} /> : <></>}
                <div>
                    {form && form.questions ? <FormLoadingContext.Provider value={[formLoading, setFormLoading]}>
                        {infoSaved && <Alert type="success" title={t("Informations enregistrées")} />}
                        <div className="z-10">
                            <Form form={form.questions} handleSubmit={handleSubmit} theme={theme} contactId={contactId} candidature={completion.objectAssociated} dealId={completion.objectAssociated?.objectId} templateId={completion.templateId} formId={completion.id} loading={loading ? true : false} msalInstance={msalInstance} currentStep={step} amountSteps={completion?.steps?.length} setStep={handleChangeStep} toTheTop={toTheTop} back={canNavigateBackTo(step - 1)} next={completion?.steps[step]?.canGoNext} nextStepBack={completion?.steps[step + 1]?.canGoBack} pipelineChange={!!(completion?.steps[step]?.pipelineEnd)} secondaryObjects={completion.secondaryObjects} />
                        </div>
                    </FormLoadingContext.Provider> : null}
                    <div className={formLoading || loading ? "absolute left-0 top-0 bottom-0 right-0 bg-white text-gray-700 z-30 opacity-75 flex justify-center items-center" : "loading-end"} style={{ borderRadius: '20px' }}>
                        <div className="loaderlogo" />
                    </div>
                </div>
            </div>
        </div>
    </div>
}

export default withTranslation()(Completion);